import { useState } from 'react';
import { Tooltip } from './Tooltip';
import { logError } from '../../services/logging/logger';

interface CopyButtonProps {
  text: string;
  label?: string;
  className?: string;
}

/**
 * Button that copies text to clipboard.
 */
export function CopyButton({ text, label = 'Copy', className = '' }: CopyButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      logError('Failed to copy to clipboard', error);
    }
  };

  return (
    <Tooltip content={copied ? 'Copied!' : label}>
      <button
        onClick={handleCopy}
        className={`
          p-2 rounded-lg transition-colors
          hover:bg-dark-300
          ${copied ? 'text-green-500' : 'text-gray-400 hover:text-white'}
          ${className}
        `}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </button>
    </Tooltip>
  );
}

interface CopyFieldProps {
  value: string;
  label?: string;
}

/**
 * Text field with copy button.
 */
export function CopyField({ value, label }: CopyFieldProps) {
  return (
    <div>
      {label && (
        <label className="block text-sm text-gray-400 mb-1">{label}</label>
      )}
      <div className="flex items-center gap-2 bg-dark-200 border border-dark-400 rounded-lg px-4 py-2">
        <span className="flex-1 font-mono text-white">{value}</span>
        <CopyButton text={value} />
      </div>
    </div>
  );
}

function CopyIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 13l4 4L19 7"
      />
    </svg>
  );
}
